import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InfoIcon from '@mui/icons-material/Info';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InventoryIcon from '@mui/icons-material/Inventory';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TuneIcon from '@mui/icons-material/Tune';
import GridViewIcon from '@mui/icons-material/GridView';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { useB2BAccess } from '../../hooks/useModuleAccess';
import { ImageUpload } from '../../components/ImageUpload';
import { RichTextEditor } from '../../components/RichTextEditor';
import { MobileFormField } from '../../components/mobile/MobileFormField';
import { ProductSuppliersTab } from './components/ProductSuppliersTab';
import { BarcodeScannerModal } from '../pos/components/BarcodeScannerModal';
import { VariantMatrixView } from './components/VariantMatrixView';
import { VariantPricingTab } from './components/VariantPricingTab';
import { VariantImagesTab } from './components/VariantImagesTab';
import { VariantInventoryTab } from './components/VariantInventoryTab';
import { VariantBulkEditor } from './components/VariantBulkEditor';
import { BulkVariantCreationDialog } from './components/BulkVariantCreationDialog';
import { generateSkuFromName } from '../../utils/sku';
import { useSkuValidation } from '../../hooks/useSkuValidation';
import type { Product, ProductImage, ProductType } from '../../types';

interface ProductFormData {
  name: string;
  description: string;
  detailedDescription: string;
  category: string;
  price: number;
  sku: string;
  barcode?: string;
  isVisible: boolean;
  isB2bVisible: boolean;
  b2bPrice: number | null;
  b2bMinimumQuantity: number | null;
  productType: ProductType;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ProductEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { hasAccess: hasB2BAccess } = useB2BAccess();
  const [tabValue, setTabValue] = useState(0);
  const [variantSubTab, setVariantSubTab] = useState(0);
  const [scannerModalOpen, setScannerModalOpen] = useState(false);
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  const [bulkCreateDialogOpen, setBulkCreateDialogOpen] = useState(false);

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
  } = useForm<ProductFormData>({
    defaultValues: {
      detailedDescription: '',
      b2bPrice: null,
      b2bMinimumQuantity: null,
    },
  });

  const isVisible = watch('isVisible');
  const isB2bVisible = watch('isB2bVisible');
  const price = watch('price');
  const b2bPrice = watch('b2bPrice');
  const productType = watch('productType');
  const productName = watch('name');
  const currentSku = watch('sku');
  const [images, setImages] = useState<ProductImage[]>([]);
  const [originalSku, setOriginalSku] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await axiosInstance.get<Product>(`/products/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        description: data.description || '',
        detailedDescription: (data as any).detailedDescription || '',
        category: data.category || '',
        price: data.price,
        sku: data.sku || '',
        barcode: data.barcode || '',
        isVisible: (data as any).isVisibleInStorefront || false,
        isB2bVisible: (data as any).isB2bVisible || false,
        b2bPrice: (data as any).b2bPrice || null,
        b2bMinimumQuantity: (data as any).b2bMinimumQuantity || null,
        productType: data.productType || 'sellable',
      });
      setImages(data.images || []);
      setOriginalSku(data.sku || '');
    }
  }, [data, reset]);

  // Real-time SKU availability validation (only if SKU changed)
  const { isAvailable, isChecking } = useSkuValidation({
    sku: currentSku,
    excludeProductId: id, // Exclude current product from check
    enabled: !!currentSku && originalSku !== currentSku, // Only check if SKU changed
  });

  // SKU regeneration handler
  const handleRegenerateSku = () => {
    if (productName && productName.trim().length > 0) {
      const newSku = generateSkuFromName(productName);
      setValue('sku', newSku);
    }
  };

  const updateMutation = useMutation({
    mutationFn: (formData: any) => axiosInstance.patch(`/products/${id}`, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showMessage('Product updated successfully', 'success');
      navigate('/app/products');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update product', 'error');
    },
  });

  const onSubmit = (formData: ProductFormData) => {
    const transformedData = {
      name: formData.name,
      description: formData.description,
      detailedDescription: formData.detailedDescription || null,
      category: formData.category,
      price: Number(formData.price),
      sku: formData.sku,
      barcode: formData.barcode || null,
      isVisibleInStorefront: formData.productType === 'sellable' ? formData.isVisible : false,
      isB2bVisible: formData.productType === 'sellable' ? formData.isB2bVisible : false,
      b2bPrice: formData.b2bPrice ? Number(formData.b2bPrice) : null,
      b2bMinimumQuantity: formData.b2bMinimumQuantity ? Number(formData.b2bMinimumQuantity) : null,
      productType: formData.productType,
    };
    updateMutation.mutate(transformedData);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Calculate discount percentage for B2B price
  const b2bDiscountPercent = price && b2bPrice ? Math.round((1 - b2bPrice / price) * 100) : 0;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load product'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit Product
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="product edit tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="General" />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TuneIcon sx={{ fontSize: 18 }} />
                    Variants
                  </Box>
                }
              />
              {hasB2BAccess && (
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 18 }} />
                      B2B Settings
                    </Box>
                  }
                />
              )}
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalShippingIcon sx={{ fontSize: 18 }} />
                    Fournisseurs
                  </Box>
                }
              />
            </Tabs>

            {/* General Tab */}
            <TabPanel value={tabValue} index={0}>
              <MobileFormField
                name="name"
                control={control}
                rules={{ required: 'Product name is required' }}
                label="Product Name"
                placeholder="Enter product name"
                margin="normal"
                fullWidth
                autoFocus
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    label="Short Description"
                    multiline
                    rows={2}
                    helperText="Brief description shown in product listings"
                  />
                )}
              />

              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detailed Description
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Rich text description with formatting (headings, lists, links). Shown on product detail page.
                </Typography>
                <Controller
                  name="detailedDescription"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Enter detailed product description with specifications, features, etc..."
                      minHeight={200}
                    />
                  )}
                />
              </Box>

              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    select
                    label="Category"
                  >
                    <MenuItem value="Electronics">Electronics</MenuItem>
                    <MenuItem value="Clothing">Clothing</MenuItem>
                    <MenuItem value="Food">Food</MenuItem>
                    <MenuItem value="Books">Books</MenuItem>
                    <MenuItem value="Home & Garden">Home & Garden</MenuItem>
                    <MenuItem value="Sports">Sports</MenuItem>
                    <MenuItem value="Toys">Toys</MenuItem>
                    <MenuItem value="Beauty">Beauty</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                )}
              />

              <MobileFormField
                name="price"
                control={control}
                rules={{
                  required: 'Price is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Price must be positive' },
                }}
                label="Retail Price (DZD)"
                type="number"
                placeholder="0.00"
                margin="normal"
                fullWidth
                inputProps={{ step: '0.01' }}
              />

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <MobileFormField
                  name="sku"
                  control={control}
                  rules={{
                    required: 'SKU is required',
                    maxLength: { value: 100, message: 'SKU must not exceed 100 characters' },
                  }}
                  label="SKU (Stock Keeping Unit)"
                  helperText={
                    isChecking
                      ? "⏳ Checking availability..."
                      : isAvailable === false
                      ? "⚠️ SKU already exists in your organization"
                      : isAvailable === true && originalSku !== currentSku
                      ? "✓ New SKU is available"
                      : "Unique product identifier"
                  }
                  placeholder="e.g. PROD-001"
                  margin="normal"
                  fullWidth
                  error={isAvailable === false}
                />
                <Tooltip title="Regenerate SKU from product name">
                  <IconButton
                    onClick={handleRegenerateSku}
                    disabled={!productName || productName.trim().length === 0}
                    sx={{ mt: 2 }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Controller
                name="barcode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    label="Code-barres (optionnel)"
                    helperText="EAN-8, EAN-13, UPC ou autre format"
                    placeholder="e.g. 1234567890123"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Scanner avec la caméra">
                            <IconButton
                              onClick={() => setScannerModalOpen(true)}
                              edge="end"
                              color="primary"
                            >
                              <CameraAltIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Product Type Selector */}
              <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }}>
                <FormLabel component="legend">Type de produit</FormLabel>
                <Controller
                  name="productType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        value="sellable"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StorefrontIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            Vendable (visible au POS)
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="internal"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <InventoryIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            Interne (usage interne uniquement)
                          </Box>
                        }
                      />
                    </RadioGroup>
                  )}
                />
                {productType === 'internal' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Les produits internes (nettoyage, sacs, fournitures) n'apparaissent pas au POS ni sur le storefront.
                  </Typography>
                )}
              </FormControl>

              {productType === 'sellable' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isVisible}
                      onChange={(e) => setValue('isVisible', e.target.checked)}
                    />
                  }
                  label="Visible on Storefront (Published)"
                  sx={{ mt: 2, display: 'block' }}
                />
              )}

              {hasB2BAccess && productType === 'sellable' && (
                <Tooltip title="When enabled, this product will be visible to approved B2B resellers in your B2B portal">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isB2bVisible}
                        onChange={(e) => setValue('isB2bVisible', e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Visible to B2B Resellers
                      </Box>
                    }
                    sx={{ mt: 1, display: 'block' }}
                  />
                </Tooltip>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Image Upload Section */}
              {id && (
                <ImageUpload
                  productId={id}
                  images={images}
                  onImagesChange={setImages}
                />
              )}
            </TabPanel>

            {/* Variants Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Product Variants</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GridViewIcon />}
                      onClick={() => setBulkCreateDialogOpen(true)}
                    >
                      Bulk Create
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setBulkEditorOpen(true)}
                    >
                      Bulk Edit
                    </Button>
                  </Box>
                </Box>

                <Tabs
                  value={variantSubTab}
                  onChange={(_, newValue) => setVariantSubTab(newValue)}
                  aria-label="variant management tabs"
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <GridViewIcon sx={{ fontSize: 16 }} />
                        Matrix View
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MonetizationOnIcon sx={{ fontSize: 16 }} />
                        Pricing
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhotoLibraryIcon sx={{ fontSize: 16 }} />
                        Images
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WarehouseIcon sx={{ fontSize: 16 }} />
                        Inventory
                      </Box>
                    }
                  />
                </Tabs>

                {/* Matrix View Sub-Tab */}
                <TabPanel value={variantSubTab} index={0}>
                  {data && <VariantMatrixView product={data} />}
                </TabPanel>

                {/* Pricing Sub-Tab */}
                <TabPanel value={variantSubTab} index={1}>
                  {id && <VariantPricingTab productId={id} />}
                </TabPanel>

                {/* Images Sub-Tab */}
                <TabPanel value={variantSubTab} index={2}>
                  {id && <VariantImagesTab productId={id} />}
                </TabPanel>

                {/* Inventory Sub-Tab */}
                <TabPanel value={variantSubTab} index={3}>
                  {id && <VariantInventoryTab productId={id} />}
                </TabPanel>
              </Box>
            </TabPanel>

            {/* B2B Settings Tab */}
            {hasB2BAccess && (
              <TabPanel value={tabValue} index={2}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon />
                    <Typography variant="body2">
                      Configure B2B-specific pricing and ordering options for your resellers.
                      Tier-based discounts from your pricing tiers will apply on top of the B2B base price.
                    </Typography>
                  </Box>
                </Alert>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isB2bVisible}
                      onChange={(e) => setValue('isB2bVisible', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography>Enable B2B Sales for this Product</Typography>
                    </Box>
                  }
                  sx={{ mb: 3, display: 'block' }}
                />

                {isB2bVisible && (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <MobileFormField
                        name="b2bPrice"
                        control={control}
                        rules={{
                          valueAsNumber: true,
                          min: { value: 0.01, message: 'B2B price must be greater than 0' },
                        }}
                        label="B2B Base Price (DZD)"
                        helperText="Base price for B2B customers. If empty, retail price is used."
                        type="number"
                        placeholder="0.00"
                        margin="normal"
                        fullWidth
                        inputProps={{ step: '0.01' }}
                        InputProps={{
                          endAdornment: b2bDiscountPercent > 0 ? (
                            <InputAdornment position="end">
                              <Typography variant="caption" color="success.main">
                                {b2bDiscountPercent}% off retail
                              </Typography>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Box>

                    <MobileFormField
                      name="b2bMinimumQuantity"
                      control={control}
                      rules={{
                        valueAsNumber: true,
                        min: { value: 1, message: 'Minimum quantity must be at least 1' },
                      }}
                      label="Minimum Order Quantity"
                      helperText="Minimum order quantity for B2B customers. Leave empty for no minimum."
                      type="number"
                      placeholder="1"
                      margin="normal"
                      fullWidth
                      inputProps={{ min: 1 }}
                    />

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Pricing Summary
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Retail Price:</Typography>
                          <Typography variant="body2">{price ? `${price.toLocaleString()} DZD` : '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">B2B Base Price:</Typography>
                          <Typography variant="body2" color="primary.main">
                            {b2bPrice ? `${b2bPrice.toLocaleString()} DZD` : `${price?.toLocaleString() || 0} DZD (retail)`}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Note: Tier discounts will be applied on top of the B2B base price when resellers place orders.
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </TabPanel>
            )}

            {/* Fournisseurs Tab */}
            <TabPanel value={tabValue} index={hasB2BAccess ? 3 : 2}>
              {id && data && (
                <ProductSuppliersTab
                  productId={id}
                  productName={data.name}
                  defaultCostPrice={(data as any)?.defaultCostPrice}
                  currentStock={data.stockQuantity || 0}
                />
              )}
            </TabPanel>

            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/app/products')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        open={scannerModalOpen}
        onClose={() => setScannerModalOpen(false)}
        onScan={(barcode) => {
          setValue('barcode', barcode);
          showMessage(`Code-barres scanné: ${barcode}`, 'success');
        }}
      />

      {/* Bulk Variant Creation Dialog */}
      {data && (
        <BulkVariantCreationDialog
          open={bulkCreateDialogOpen}
          onClose={() => setBulkCreateDialogOpen(false)}
          product={data}
        />
      )}

      {/* Variant Bulk Editor */}
      {id && (
        <VariantBulkEditor
          open={bulkEditorOpen}
          onClose={() => setBulkEditorOpen(false)}
          productId={id}
        />
      )}
    </Box>
  );
};
